import { useEffect, useState } from "react";
import { Quote, ChevronDown, ArrowUp, ArrowDown, Trash2, Check, EyeOff, Plus } from "lucide-react";
import api from "../lib/api";
import { useFlash } from "../lib/flash";
import { fmtShortDate } from "../lib/format";
import LogoUpload from "./LogoUpload";
import { TField } from "./TestimonialCard";

const ST = { pending: ["pending", "Afventer"], approved: ["picked_up", "Godkendt"], hidden: ["cancelled", "Skjult"] };
const EMPTY = { quote: "", person_name: "", role: "", shop_name: "", city: "", consent_method: "", consent_date: "" };

function Editor({ t, methods, onDone }) {
  const flash = useFlash();
  const [v, setV] = useState(t ? { ...t, consent_method: t.consent?.given_by === "admin" ? t.consent.method : "", consent_date: t.consent?.given_by === "admin" ? (t.consent.given_at || "").slice(0, 10) : "" } : EMPTY);
  const [errors, setErrors] = useState({});
  const manual = !t || t.source === "manual";
  const save = async (e) => {
    e.preventDefault();
    setErrors({});
    const body = { ...v, consent_method: v.consent_method || null, consent_date: v.consent_date || null };
    try {
      const r = t ? await api.put(`/owner/testimonials/${t.id}`, body) : await api.post("/owner/testimonials", body);
      flash(r.data.flash);
      onDone(true);
    } catch (ex) {
      if (ex.response?.data?.errors) setErrors(ex.response.data.errors);
      else flash(ex.response?.data?.detail || "Kunne ikke gemme", "error");
    }
  };
  const tid = t ? `ot-edit-${t.id}` : "ot-new";
  return (
    <form className="ot-editor" onSubmit={save} noValidate data-testid={tid}>
      {t && t.original_quote !== v.quote && <p className="ot-orig" data-testid={`${tid}-original`}>Original: "{t.original_quote}"</p>}
      {t && t.source === "app" && <p className="muted ot-note">Ret kun stavefejl – indholdet er godkendt af butikken.</p>}
      <TField k="quote" label="Citat" v={v} setV={setV} errors={errors} max={280} area testid={tid} />
      <div className="tc-grid">
        <TField k="person_name" label="Navn" v={v} setV={setV} errors={errors} max={80} testid={tid} />
        <TField k="role" label="Rolle" v={v} setV={setV} errors={errors} max={60} testid={tid} />
        <TField k="shop_name" label="Butik" v={v} setV={setV} errors={errors} max={100} testid={tid} />
        <TField k="city" label="By" v={v} setV={setV} errors={errors} max={60} testid={tid} />
      </div>
      {manual && (
        <div className="tc-grid ot-consent">
          <div className="field">
            <label htmlFor={`${tid}-cm`}>Tilladelse givet (påkrævet før godkendelse)</label>
            <select id={`${tid}-cm`} value={v.consent_method} onChange={(e) => setV({ ...v, consent_method: e.target.value })} data-testid={`${tid}-consent-method`}>
              <option value="">Vælg metode…</option>
              {methods.filter((m) => m !== "i appen").map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            {errors.consent_method && <div className="err">{errors.consent_method}</div>}
          </div>
          <div className="field">
            <label htmlFor={`${tid}-cd`}>Dato for tilladelse</label>
            <input id={`${tid}-cd`} type="date" value={v.consent_date} onChange={(e) => setV({ ...v, consent_date: e.target.value })} data-testid={`${tid}-consent-date`} />
            {errors.consent_date && <div className="err">{errors.consent_date}</div>}
          </div>
        </div>
      )}
      <LogoUpload url={v.logo_url} onChange={(id, url) => setV({ ...v, logo_id: id, logo_url: url })} testid={`${tid}-logo`} />
      <div className="ot-btns">
        <button className="btn primary small" data-testid={`${tid}-save`}>Gem</button>
        <button type="button" className="btn small" onClick={() => onDone(false)} data-testid={`${tid}-cancel`}>Annullér</button>
      </div>
    </form>
  );
}

function consentText(c) {
  if (!c) return "Ingen tilladelse";
  if (c.withdrawn_at) return `Trukket tilbage ${fmtShortDate(c.withdrawn_at)}`;
  if (!c.given) return "Ingen dokumenteret tilladelse";
  return `Tilladelse ${c.method} · ${fmtShortDate(c.given_at)} · ${c.given_by}`;
}

export default function OwnerTestimonials() {
  const flash = useFlash();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/owner/testimonials").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const act = async (fn) => {
    try { const r = await fn(); if (r.data.flash) flash(r.data.flash); }
    catch (ex) { flash(ex.response?.data?.detail || "Fejl", "error"); }
    load();
  };
  const move = (i, dir) => {
    const ids = d.items.map((t) => t.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    act(() => api.post("/owner/testimonials/reorder", { ids }));
  };
  const pending = d.items.filter((t) => t.status === "pending").length;
  const done = (ok) => { setEdit(null); if (ok) load(); };

  return (
    <section className="panel" data-testid="owner-testimonials">
      <button className="ss-head" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="owner-testimonials-toggle">
        <Quote size={18} /> <b>Udtalelser</b>
        {pending > 0 && <span className="pill pending" data-testid="owner-testimonials-pending">{pending} afventer</span>}
        <ChevronDown size={18} style={{ marginLeft: "auto", transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      {open && (
        <div className="ot">
          <p className="muted ot-note">Kun godkendte udtalelser med dokumenteret tilladelse vises på /priser.</p>
          {edit === "new" ? <Editor methods={d.methods} onDone={done} /> : (
            <button type="button" className="btn small" onClick={() => setEdit("new")} data-testid="owner-testimonial-new"><Plus size={14} /> Opret manuelt</button>
          )}
          {!d.items.length && <p className="muted" data-testid="owner-testimonials-empty">Ingen udtalelser endnu.</p>}
          <ol className="ot-list">
            {d.items.map((t, i) => (
              <li key={t.id} data-testid={`owner-testimonial-${t.id}`}>
                {edit === t.id ? <Editor t={t} methods={d.methods} onDone={done} /> : (
                  <>
                    <div className="ot-top">
                      <span className={`pill ${ST[t.status][0]}`} data-testid={`owner-testimonial-status-${t.id}`}>{ST[t.status][1]}</span>
                      <small className={t.consent_ok ? "muted" : "ot-bad"} data-testid={`owner-testimonial-consent-${t.id}`}>{consentText(t.consent)}</small>
                      <small className="muted">{t.source === "app" ? "Fra appen" : "Manuel"}</small>
                    </div>
                    <blockquote>"{t.quote}"</blockquote>
                    {t.original_quote !== t.quote && <p className="ot-orig">Original: "{t.original_quote}"</p>}
                    <p className="muted ot-by">– {t.person_name}{t.role ? `, ${t.role}` : ""} · {t.shop_name}{t.city ? `, ${t.city}` : ""}</p>
                    <div className="ot-btns">
                      {t.status !== "approved" && (
                        <button type="button" className="btn small primary" disabled={!t.consent_ok} title={t.consent_ok ? "" : "Kræver dokumenteret tilladelse"}
                          onClick={() => act(() => api.post(`/owner/testimonials/${t.id}/status`, { status: "approved" }))} data-testid={`owner-testimonial-approve-${t.id}`}>
                          <Check size={14} /> Godkend
                        </button>
                      )}
                      {t.status !== "hidden" && (
                        <button type="button" className="btn small" onClick={() => act(() => api.post(`/owner/testimonials/${t.id}/status`, { status: "hidden" }))} data-testid={`owner-testimonial-hide-${t.id}`}>
                          <EyeOff size={14} /> Skjul
                        </button>
                      )}
                      <button type="button" className="btn small" onClick={() => setEdit(t.id)} data-testid={`owner-testimonial-edit-${t.id}`}>Rediger</button>
                      <button type="button" className="btn small" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Flyt op" data-testid={`owner-testimonial-up-${t.id}`}><ArrowUp size={14} /></button>
                      <button type="button" className="btn small" disabled={i === d.items.length - 1} onClick={() => move(i, 1)} aria-label="Flyt ned" data-testid={`owner-testimonial-down-${t.id}`}><ArrowDown size={14} /></button>
                      <button type="button" className="btn small danger" onClick={() => window.confirm("Slet udtalelsen?") && act(() => api.delete(`/owner/testimonials/${t.id}`))} data-testid={`owner-testimonial-delete-${t.id}`}>
                        <Trash2 size={14} /> Slet
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}
